export type RestoreStatus =
    | "QUEUED"
    | "VALIDATING"
    | "CLEARING"
    | "RESTORING"
    | "SUCCEEDED"
    | "FAILED";

export type RestoreEntityType =
    | "USERS"
    | "CAMPAIGNS"
    | "SPELLS"
    | "CHARACTERS"
    | "SCENES"
    | "DUNGEONS"
    | "ENCOUNTERS";

export interface EntityRestoreProgress {
    processed: number;
    total: number;
}

export interface RestoreProgressEvent {
    jobId: string;
    backupId: string;
    revision: number;
    status: RestoreStatus;
    progress: Record<RestoreEntityType, EntityRestoreProgress>;
    error?: string | null;
    finished: boolean;
    failed: boolean;
}

export type BackupCreationStatus = "QUEUED" | "EXPORTING" | "FINALIZING" | "SUCCEEDED" | "FAILED";
export interface BackupCreationProgressEvent extends Omit<RestoreProgressEvent, "status"> {
    status: BackupCreationStatus;
}

export function calculateEntityPercentage(progress: Record<RestoreEntityType, EntityRestoreProgress>): number {
    const totals = Object.values(progress).reduce((result, value) => ({processed: result.processed + value.processed, total: result.total + value.total}), {processed: 0, total: 0});
    return totals.total === 0 ? 0 : Math.min(1, Math.max(0, totals.processed / totals.total));
}

export function calculateOverallCreationPercentage(event: BackupCreationProgressEvent): number {
    if (event.status === "SUCCEEDED") return 100;
    if (event.status === "FINALIZING") return 95;
    if (event.status !== "EXPORTING") return 0;
    return Math.round(calculateEntityPercentage(event.progress) * 95);
}

// Validierung und das Leeren der Datenbank liefern noch keine eigenen Zähler.
// Sie werden deshalb als feste Meilensteine gewichtet. Nach dem Entity-Import
// bleibt außerdem ein Anteil für Flush und Transaktionsabschluss reserviert.
const VALIDATION_STARTED_PERCENTAGE = 5;
const VALIDATION_COMPLETED_PERCENTAGE = 15;
const CLEARING_COMPLETED_PERCENTAGE = 25;
const ENTITY_IMPORT_COMPLETED_PERCENTAGE = 95;
const ENTITY_IMPORT_PERCENTAGE = ENTITY_IMPORT_COMPLETED_PERCENTAGE - CLEARING_COMPLETED_PERCENTAGE;

export function calculateOverallRestorePercentage(event: RestoreProgressEvent): number {
    switch (event.status) {
        case "QUEUED":
            return 0;
        case "VALIDATING":
            return VALIDATION_STARTED_PERCENTAGE;
        case "CLEARING":
            return VALIDATION_COMPLETED_PERCENTAGE;
        case "SUCCEEDED":
            return 100;
        case "FAILED":
            return calculateFailedRestorePercentage(event);
        case "RESTORING":
            return calculateEntityImportPercentage(event);
    }
}

function calculateEntityImportPercentage(event: RestoreProgressEvent): number {
    const entityProgress = calculateEntityPercentage(event.progress);
    return Math.round(CLEARING_COMPLETED_PERCENTAGE + entityProgress * ENTITY_IMPORT_PERCENTAGE);
}

function calculateFailedRestorePercentage(event: RestoreProgressEvent): number {
    const hasStartedEntityImport = Object.values(event.progress).some(progress => progress.processed > 0);
    return hasStartedEntityImport ? calculateEntityImportPercentage(event) : 0;
}
